import React, { ChangeEventHandler, ChangeEvent, ReactNode } from "react";
import {
  Grid,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";
import dayjs from "dayjs";
import ReplayIcon from "@mui/icons-material/Replay";
import { CloudDownload } from "iconoir-react";
import {
  RestrictionStatus,
  RestrictionStatusNaming,
  ConsiderationStatus,
  ConsiderationStatusNaming,
} from "../../constant/upliftConstant";
import ExportDialog from "./ExportDialog";
import useBoolean from "../../hooks/useBoolean";

export type FilterValue = {
  adviser?: string;
  includeNoActionRequired?: any;
  name?: string;
  personId?: string;
  upliftDateFrom?: any;
  upliftDateTo?: any;
  consideration?: any;
  restrictions?: string;
};

interface FilterProps {
  value: FilterValue;
  loading?: boolean;
  onChange: (f: FilterValue, triggerFetch?: boolean) => void;
  onSearch: () => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  value,
  loading,
  onChange,
  onSearch,
  onReset,
}) => {
  const [
    exportDialogOpen,
    { setTrue: openExportDialog, setFalse: closeExportDialog },
  ] = useBoolean(false);
  const handleAdviserChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const adviser = e.target.value;
    onChange({
      ...value,
      adviser,
    });
  };
  const handleInvestorNameChange: ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const name = e.target.value;
    onChange({
      ...value,
      name,
    });
  };
  const handleInvestorIdChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const personId = e.target.value;
    onChange({
      ...value,
      personId,
    });
  };
  const handleRestrictionsChange: ChangeEventHandler<HTMLInputElement> = (
    e: SelectChangeEvent<string>
  ) => {
    const restrictions = e.target.value;
    onChange({
      ...value,
      restrictions,
    });
  };
  const handleConsiderationsChange: ChangeEventHandler<HTMLInputElement> = (
    e: SelectChangeEvent<any>
  ) => {
    const considerationArr = e.target.value;
    onChange({
      ...value,
      consideration: considerationArr.length > 0 ? considerationArr.join() : "",
    });
  };
  const handleNoActionRequiredChange: ChangeEventHandler<HTMLInputElement> = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const includeNoActionRequired = e.target.checked;
    onChange(
      {
        ...value,
        includeNoActionRequired,
      },
      true
    );
  };
  const handleStartDateChange = (newDate: Date | null) => {
    const upliftDateFrom = formatDateToString(newDate);
    const upliftDate = {
      upliftDateTo: value.upliftDateTo,
      upliftDateFrom,
    };
    if (
      value.upliftDateTo &&
      newDate &&
      dayjs(value.upliftDateTo).isBefore(dayjs(upliftDateFrom))
    ) {
      upliftDate.upliftDateTo = null;
    }
    onChange({
      ...value,
      ...upliftDate,
    });
  };
  const handleEndDateChange = (newDate: Date | null) => {
    const upliftDateTo = formatDateToString(newDate);
    const upliftDate = {
      upliftDateFrom: value.upliftDateFrom,
      upliftDateTo,
    };
    if (
      newDate &&
      value.upliftDateFrom &&
      dayjs(upliftDateTo).isBefore(dayjs(value.upliftDateFrom))
    ) {
      upliftDate.upliftDateFrom = null;
    }
    onChange({
      ...value,
      ...upliftDate,
    });
  };
  const restrictionsOptions = getRestrictionsOptions();
  const considerationsOptions = getConsiderationsOptions();
  const renderOptions = (options: SingleOption[]) => {
    return options.map((el): ReactNode => {
      return (
        <MenuItem key={el.value} value={el.value}>
          {el.label}
        </MenuItem>
      );
    });
  };

  const parseDateString = (dateString: string): Date | null => {
    if (!dateString) return null;
    const parsedDate = parse(dateString, "yyyyMMdd", new Date());
    return parsedDate.toString() === "Invalid Date" ? null : parsedDate;
  };

  const formatDateToString = (date: Date | null): string => {
    return date ? format(date, "yyyyMMdd") : "";
  };

  return (
    <Grid container spacing={3} alignItems="center" sx={{ width: "100%" }}>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Filter by Adviser"
          size="small"
          value={value.adviser || ""}
          disabled={loading}
          onChange={handleAdviserChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          select
          size="small"
          fullWidth
          value={value.restrictions || ""}
          disabled={loading}
          label="Filter by Restrictions"
          SelectProps={{
            native: false,
          }}
          onChange={handleRestrictionsChange}
        >
          {renderOptions(restrictionsOptions)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControlLabel
          control={
            <Switch
              disabled={loading}
              checked={value.includeNoActionRequired}
              onChange={handleNoActionRequiredChange}
            />
          }
          label="Include No Action Required"
          sx={{ mb: 2 }}
        />
      </Grid>
      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              disabled={loading}
              label="Uplift start date"
              value={parseDateString(value.upliftDateFrom)}
              onChange={handleStartDateChange}
              maxDate={new Date()}
              format="dd/MM/yyyy"
            />
            <DatePicker
              disabled={loading}
              label="Uplift end date"
              value={parseDateString(value.upliftDateTo)}
              onChange={handleEndDateChange}
              maxDate={new Date()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  helperText:
                    !!value.upliftDateTo && !!value.upliftDateFrom
                      ? dayjs(value.upliftDateTo)?.isBefore(
                        dayjs(value.upliftDateFrom)
                      )
                        ? "The end date cannot be earlier than the start time"
                        : ""
                      : "",
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Filter by Investor Name"
          size="small"
          value={value.name || ""}
          disabled={loading}
          onChange={handleInvestorNameChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Filter by Investor Id"
          size="small"
          value={value.personId || ""}
          disabled={loading}
          onChange={handleInvestorIdChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          select
          size="small"
          SelectProps={{
            multiple: true,
            native: false,
          }}
          value={value.consideration ? value.consideration?.split(",") : []}
          label="Filter by Considerations"
          disabled={loading}
          fullWidth
          onChange={handleConsiderationsChange}
        >
          {renderOptions(considerationsOptions)}
        </TextField>
      </Grid>
      <Grid item sx={{ flexGrow: 1 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Button color="primary" disabled={loading} onClick={onSearch}>
            Search
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<CloudDownload />}
            disabled={loading}
            onClick={openExportDialog}
          >
            Export
          </Button>
          <Button disabled={loading} variant="outlined" onClick={onReset}>
            <ReplayIcon />
          </Button>
        </Stack>
      </Grid>
      <ExportDialog
        open={exportDialogOpen}
        onClose={closeExportDialog}
        filter={value}
      />
    </Grid>
  );
};

interface SingleOption {
  label: string;
  value: string;
  fullName: string;
}

function getRestrictionsOptions(): SingleOption[] {
  const restrictionStatus = [
    RestrictionStatus.None,
    RestrictionStatus.UPLR,
    RestrictionStatus.UPLF,
  ] as const;
  return restrictionStatus.map((status) => ({
    label: RestrictionStatusNaming[status],
    value: status,
    fullName: RestrictionStatusNaming[status],
  }));
}

function getConsiderationsOptions(): SingleOption[] {
  const considerationStatus = [
    ConsiderationStatus.RI,
    ConsiderationStatus.RC,
    ConsiderationStatus.PCLS,
  ] as const;
  return considerationStatus.map((status) => ({
    label: status,
    value: status,
    fullName: ConsiderationStatusNaming[status],
  }));
}

export default Filter;
